export class SSEError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
  }
}

interface StreamOptions {
  token: string;
  body: unknown;
}

/**
 * Perform a POST request that returns Server-Sent Events and expose an async iterator of events.
 * Keeps fetch logic isolated so UI hooks stay clean.
 */
export async function* streamSSE<T>(url: string, { token, body }: StreamOptions): AsyncGenerator<T, void, unknown> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new SSEError(`HTTP error ${response.status}`, response.status);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new SSEError("No response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";
    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6);
      if (data === "[DONE]") continue;
      try {
        yield JSON.parse(data) as T;
      } catch {
        // ignore malformed chunks
      }
    }
  }
} 